import React, { useState } from 'react';

const AccountSettings = ({
  initialEmail,
  onSaveEmail,
  onSavePassword,
  onGenerateTempPassword,
  onUserUpdated,
  currentUser,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [newPassword, setNewPassword] = useState(null);

  const [emailInput, setEmailInput] = useState(initialEmail || '');
  const [emailConfirm, setEmailConfirm] = useState(initialEmail || '');

  const [pwInput, setPwInput] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    const email = (emailInput || '').trim().toLowerCase();
    const confirm = (emailConfirm || '').trim().toLowerCase();

    if (!email) return setError('Email is required');
    if (!email.includes('@')) return setError('Please enter a valid email address');
    if (email !== confirm) return setError('Email addresses do not match');

    setLoading(true);
    try {
      const res = await onSaveEmail(email);
      setSuccessMsg('Email updated successfully.');
      if (onUserUpdated) {
        onUserUpdated({ ...currentUser, email: (res && res.email) || email });
      }
    } catch (err) {
      setError(err.message || 'Failed to update email');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);

    if (!pwInput) return setError('Password is required');
    if (pwInput.length < 8) return setError('Password must be at least 8 characters');
    if (pwInput !== pwConfirm) return setError('Passwords do not match');

    setLoading(true);
    try {
      await onSavePassword(pwInput);
      setSuccessMsg('Password updated successfully.');
      setPwInput('');
      setPwConfirm('');
    } catch (err) {
      setError(err.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!onGenerateTempPassword) return;
    setError(null);
    setSuccessMsg(null);
    setNewPassword(null);
    setLoading(true);
    try {
      const res = await onGenerateTempPassword();
      setNewPassword(res && res.password ? res.password : null);
      setSuccessMsg('Temporary password generated.');
    } catch (err) {
      setError(err.message || 'Failed to generate password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-xl shadow-lg max-w-2xl">
      <h2 className="text-2xl font-bold mb-6 text-indigo-700">Account Settings</h2>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>}
      {successMsg && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{successMsg}</div>}

      <div className="mb-8 pb-8 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Update Email</h3>
        <form onSubmit={handleEmailSubmit} className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New email address</label>
            <input
              type="email"
              required
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg"
              placeholder="name@example.com"
              autoComplete="email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm email address</label>
            <input
              type="email"
              required
              value={emailConfirm}
              onChange={(e) => setEmailConfirm(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg"
              placeholder="name@example.com"
              autoComplete="email"
            />
          </div>

          <button type="submit" disabled={loading} className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50">
            {loading ? 'Saving...' : 'Save'}
          </button>
        </form>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Update Password</h3>
        <form onSubmit={handlePasswordSubmit} className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
            <input
              type="password"
              required
              value={pwInput}
              onChange={(e) => setPwInput(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
            <input
              type="password"
              required
              value={pwConfirm}
              onChange={(e) => setPwConfirm(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="flex items-center space-x-3">
            <button type="submit" disabled={loading} className="bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50">
              {loading ? 'Saving...' : 'Save'}
            </button>
            {onGenerateTempPassword && (
              <button type="button" onClick={handleReset} disabled={loading} className="bg-red-600 text-white px-3 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50">
                {loading ? '...' : 'Generate Temp Password'}
              </button>
            )}
          </div>

          {newPassword && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
              <div className="text-sm text-gray-700">Temporary password (show once):</div>
              <div className="mt-2 font-mono text-sm bg-white p-2 rounded">{newPassword}</div>
              <div className="text-xs text-gray-500 mt-2">Please share this password with the user so they can sign in and change it.</div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default AccountSettings;